import ChatList from '@/components/chat/ChatList';
import ChatWindow from '@/components/chat/ChatWindow';
import OAuthErrorState from '@/components/chat/OAuthErrorState';
import OrderDetailsChatPanel from '@/components/orders/OrderDetailsChatPanel';
import { useChat } from '@/contexts/ChatContext';
import { ChatStompProvider } from '@/contexts/chat-stomp-context';
import { useIsMobileOrTablet } from '@/hooks/use-mobile';
import { toast } from '@/hooks/use-toast';
import { useWebSocketChannel } from '@/hooks/useWebSocketChannel';
import { getPageId } from '@/services/utl-service';
import { RefreshCw } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function ChatPage() {
  const { t } = useTranslation('chat');
  const {
    onMessage,
    messages,
    selectConversation,
    currentConversation,
    isLoadingMessage,
    isLoadingMore,
    conversations,
    loadingConversations,
    isLoadingMoreConversations,
    hasMoreConversations,
    onLoadMoreConversations,
    onLoadMoreMessage,
    messageAfterCursor,
    oauthError,
    reconnectFacebook,
    refetchConversation,
    handleGlobalIncomingMessage,
  } = useChat();
  const [isReconnecting, setIsReconnecting] = useState(false);
  const isMobileOrTablet = useIsMobileOrTablet();

  // On mobile: show list only until user picks a conversation
  useEffect(() => {
    if (isMobileOrTablet) {
      selectConversation(null);
    }
  }, [isMobileOrTablet, selectConversation]);

  // ── Global new-message subscription (page-level topic) ───────────────────
  // Uses the existing useWebSocketChannel hook — same pattern as sale-sidebar-tabs.
  // handleGlobalIncomingMessage (from ChatContext) does all routing:
  //   • active conversation  → routes into onMessage (no toast)
  //   • inactive conversation → updates sidebar unreadCount/snippet, returns humanId
  const pageId = getPageId();
  const globalMessageTopic = pageId ? `/topic/conversations/${pageId}` : null;

  const onGlobalMessage = React.useCallback((msg: { body: string }) => {
    const humanId = handleGlobalIncomingMessage(msg.body);
    if (!humanId) return; // was for the active conversation — already handled

    // Find sender name from the conversation list for the toast
    const conv = conversations.find((c) => c.getUser()?.id === humanId);
    const senderName = conv?.getUser()?.name ?? t('newMessage') ?? 'New message';
    const snippet = conv?.snippet ?? '';

    toast({
      title: `💬 ${senderName}`,
      description: snippet || t('newMessageReceived') || 'Sent you a message',
      duration: 5000,
    });
  }, [handleGlobalIncomingMessage, conversations, t]);

  useWebSocketChannel(globalMessageTopic, onGlobalMessage);

  // Mobile/Tablet view state logic
  const showListView = isMobileOrTablet && !currentConversation;
  const showChatView = isMobileOrTablet && !!currentConversation;

  const handleBack = () => {
    selectConversation(null);
  };

  return (
    <div className="flex h-full min-h-0 w-full overflow-hidden">
      {/* Conversation sidebar */}
      {(showListView || !isMobileOrTablet) && (
        <div className="flex flex-col w-full md:w-80 xl:w-96 border-r border-gray-200 flex-shrink-0 h-full overflow-hidden">
          <div className="px-4 py-4 border-b border-gray-200 flex-shrink-0 flex items-center justify-between gap-2">
            <h2 className="text-xl font-bold text-gray-900">{t('title')}</h2>
            <button
              type="button"
              onClick={() => void refetchConversation?.()}
              disabled={loadingConversations}
              className="p-2 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 disabled:opacity-50 disabled:pointer-events-none transition-colors"
              aria-label={t('refreshConversations')}
            >
              <RefreshCw
                className={`h-5 w-5 ${loadingConversations ? 'animate-spin' : ''}`}
                aria-hidden
              />
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-hidden">
            <ChatList
              onSelectConversation={selectConversation}
              currentConversation={currentConversation}
              conversations={conversations}
              loadingConversations={loadingConversations}
              isLoadingMore={isLoadingMoreConversations}
              hasMore={hasMoreConversations}
              onLoadMore={onLoadMoreConversations}
            />
          </div>
        </div>
      )}

      {/* Main chat area */}
      {(showChatView || !isMobileOrTablet) && (
        <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
          {oauthError && !loadingConversations ? (
            <OAuthErrorState
              onReconnect={async () => {
                setIsReconnecting(true);
                try {
                  await reconnectFacebook();
                } finally {
                  setIsReconnecting(false);
                }
              }}
              isLoading={isReconnecting}
            />
          ) : currentConversation ? (
            <ChatStompProvider>
              <ChatWindow
                key={currentConversation.id}
                currentConversation={currentConversation}
                messages={messages}
                isLoadingMessage={isLoadingMessage}
                isLoadingMore={isLoadingMore}
                OnNewMessage={onMessage}
                OnLoadMoreMessage={onLoadMoreMessage}
                prevCursor={messageAfterCursor}
                hideHeader={false}
                onBack={isMobileOrTablet ? handleBack : undefined}
              />
            </ChatStompProvider>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-sm select-none">
              <div className="text-center space-y-2">
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto">
                  <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <p className="font-medium text-gray-500">Select a conversation</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Order/customer details panel — xl+ only */}
      {currentConversation && (
        <div className="hidden xl:flex flex-col w-80 border-l border-gray-200 flex-shrink-0 h-full overflow-hidden">
          <OrderDetailsChatPanel currentCustomer={currentConversation.getUser()!} />
        </div>
      )}
    </div>
  );
}
